import React from 'react';

export interface DonutChartSegment {
  value: number;
  color: string;
}

export interface DonutChartProps {
  segments: DonutChartSegment[];
  total: number;
  unit?: string;
  size?: number;
}

/**
 * DonutChart renders a donut-style pie chart with a central label for total value.
 * Segments are colored as specified. The chart is SVG-based and responsive.
 */
export const DonutChart: React.FC<DonutChartProps> = ({ segments, total, unit = 'GB', size = 140 }) => {
  const strokeWidth = 30;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const totalValue = segments.reduce((sum, seg) => sum + seg.value, 0);
  const circumference = 2 * Math.PI * radius;

  // Create a visible 2px space between each segment by reducing arc length in strokeDasharray
  const gap = 2; // px gap between segments
  let cumulative = 0;
  const circles = segments.map((seg) => {
    const value = seg.value;
    const angle = (value / totalValue) * 360;
    const offset = (cumulative / totalValue) * 360;
    cumulative += value;
    // Arc length for this segment, minus the gap
    const arc = Math.max(0, (angle / 360) * circumference - gap);
    const rest = circumference - arc;
    const dashoffset = -offset / 360 * circumference;
    return (
      <circle
        key={`${seg.color}-${seg.value}`}
        r={radius}
        cx={center}
        cy={center}
        fill="none"
        stroke={seg.color}
        strokeWidth={strokeWidth}
        strokeDasharray={`${arc} ${rest}`}
        strokeDashoffset={dashoffset}
        strokeLinecap="butt"
        style={{ transition: 'stroke-dasharray 0.3s, stroke-dashoffset 0.3s' }}
      />
    );
  });

  return (
    <div style={{ width: size, height: size, position: 'relative', display: 'inline-block' }}>
      <svg width={size} height={size} style={{ display: 'block' }} aria-hidden="true">
        {circles}
        <circle
          r={radius - strokeWidth / 2}
          cx={center}
          cy={center}
          fill="#fff"
          stroke="none"
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: size,
          height: size,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div style={{ fontSize: 28, fontFamily: 'ATTAleckSansMd', color: '#13171B' }}>{total.toFixed(2)}</div>
        <div style={{ fontSize: 21, fontFamily: 'ATTAleckSansMd', color: '#1D2329' }}>{unit}</div>
      </div>
    </div>
  );
};
